import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { getUserProfile, UserProfile } from '../data/mockProfiles';
import './Sidebar.css';

// TypeScript interfaces for Sidebar component
interface MenuItem {
  icon: string;
  label: string;
  active: boolean;
}

interface MenuSection {
  section: string;
  items: MenuItem[];
}

interface SidebarProps {
  isOpen: boolean;
  onToggle: (open: boolean) => void;
  userRole?: string;
  onLogout?: () => void;
}

// Sidebar Component - Left navigation and menu sections
// Contains quicklinks to main features, settings, and help
// Animation: Slides in from left with staggered menu items
// TypeScript: Type-safe props and menu data structures
const Sidebar: React.FC<SidebarProps> = ({ userRole = 'agent', onLogout }) => {
  const [showProfileMenu, setShowProfileMenu] = useState<boolean>(false);
  const userProfile: UserProfile = getUserProfile(userRole);

  const sidebarVariants = {
    hidden: { x: -280, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  const itemVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: (index: number) => ({
      x: 0,
      opacity: 1,
      transition: { delay: 0.1 + index * 0.05, duration: 0.4 }
    })
  };

  const menuItems: MenuSection[] = [
    {
      section: '👤 Navigation',
      items: [
        { icon: '⌂', label: 'Dashboard', active: true },
        { icon: '📱', label: 'My Apps', active: false }
      ]
    },
    {
      section: '🔧 Settings',
      items: [
        { icon: '⚙', label: 'Admin Console', active: false },
        { icon: '🔐', label: 'Security Settings', active: false },
        { icon: '👥', label: 'Users & Groups', active: false }
      ]
    },
    {
      section: '📊 Modules',
      items: [
        { icon: '💼', label: 'HRMS Portal', active: false },
        { icon: '📈', label: 'Analytics', active: false }
      ]
    }
  ];

  let itemIndex = 0;

  const handleProfileClick = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <motion.aside
      className="sidebar"
      variants={sidebarVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Navigation content wrapper */}
      <div className="sidebar-content">
        {/* Branding moved here from top header */}
        <div className="sidebar-brand">
          <div className="brand-icon">⚙</div>
          <div className="brand-text">
            <div className="brand-title">Antasis Ecosystem</div>
            <div className="brand-sub">Identity</div>
          </div>
        </div>
        {menuItems.map((section, sectionIdx) => (
          <motion.div key={sectionIdx} className="sidebar-section-group">
            <div className="sidebar-label">{section.section}</div>
            <div className="sidebar-section">
              {section.items.map((item, itemIdx) => {
                const currentIndex = itemIndex++;
                return (
                  <motion.a
                    key={itemIdx}
                    href="#"
                    className={`sidebar-item ${item.active ? 'active' : ''}`}
                    custom={currentIndex}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{ x: 4, transition: { duration: 0.2 } }}
                  >
                    <span className="sidebar-icon">{item.icon}</span>
                    <span>{item.label}</span>
                  </motion.a>
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>

      {/* User Profile Section - Bottom of sidebar */}
      <div className="sidebar-footer">
        <div className="profile-section">
          <motion.button
            className="profile-avatar-btn"
            onClick={handleProfileClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="profile-avatar">{userProfile.avatar}</div>
            <div className="profile-info">
              <div className="profile-name">{userProfile.name}</div>
              <div className="profile-role">{userProfile.role}</div>
            </div>
          </motion.button>

          {/* Profile Menu Dropdown */}
          <motion.div
            className="profile-menu"
            initial={{ opacity: 0, y: 10 }}
            animate={showProfileMenu ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            style={{ pointerEvents: showProfileMenu ? 'auto' : 'none' }}
          >
            <div className="profile-details">
              <div className="profile-detail-item">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{userProfile.email}</span>
              </div>
              <div className="profile-detail-item">
                <span className="detail-label">Department:</span>
                <span className="detail-value">{userProfile.department}</span>
              </div>
              <div className="profile-detail-item">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{userProfile.phone}</span>
              </div>
              <div className="profile-detail-item">
                <span className="detail-label">Last Login:</span>
                <span className="detail-value">{userProfile.lastLogin}</span>
              </div>
            </div>

            <div className="profile-menu-divider" />

            <button className="profile-menu-item view-profile">
              👤 View Full Profile
            </button>
            <button
              className="profile-menu-item logout"
              onClick={handleLogout}
            >
              🚪 Sign Out
            </button>
          </motion.div>
        </div>
      </div>
    </motion.aside>
  );
};

export default Sidebar;
